import {Selector, State} from '@ngxs/store';
import {ModalMediaStateModel} from '@store/modals-state/media/modal-media-state.model';
import {MediaTypeEnum} from '@shared/enums/media-type.enum';
import {IMediaItem} from '@interfaces/media';
import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {MediaService} from '@services/media/media.service';
import {finalize, tap} from 'rxjs/operators';
import {OrderType} from '../../../types/order.type';
import {IFolder, IFolderContent, IFolderPath} from '@interfaces/folder';
import {MediaSortType} from '../../../types/media.type';
import {NgxsDataRepository} from '@angular-ru/ngxs/repositories';
import {Payload, StateRepository} from '@angular-ru/ngxs/decorators';
import {DataActionWithoutSubscription} from '@shared/functions';

@StateRepository()
@State<ModalMediaStateModel>({
    name: 'modalMediaState',
    defaults: {
        mediaType: {
            selectedMediaTypes: '',
            activeTabMediaType: '',
        },
        currentPage: 1,
        medias: [],
        search: '',
        itemsPerPage: 50,
        totalMedia: 0,
        loader: false,
        order: 'DESC',
        currentDirectory: null,
        sort: 'created_at',
        folders: [],
        onlyMedias: false,
        onlyFolders: false,
    }
})

@Injectable()
export class ModalMediaState extends NgxsDataRepository<ModalMediaStateModel> {
    constructor(private mediaService: MediaService) {
        super();
    }

    @Selector([ModalMediaState])
    static getLoaderStatus(state: ModalMediaStateModel): boolean {
        return state.loader;
    }

    @Selector([ModalMediaState])
    static getSearch(state: ModalMediaStateModel): string {
        return state.search;
    }

    @Selector([ModalMediaState])
    static getMedia(state: ModalMediaStateModel): IMediaItem[] {
        return state.medias;
    }

    @Selector([ModalMediaState])
    static getFolders(state: ModalMediaStateModel): IFolder[] {
        return state.folders;
    }

    @Selector([ModalMediaState])
    static getSelectedMediaType(state: ModalMediaStateModel): MediaTypeEnum | '' {
        return state.mediaType.activeTabMediaType;
    }


    @DataActionWithoutSubscription({cancelUncompleted: true})
    fetchMedia(): Observable<IFolderContent<IMediaItem>> {
        const limit = this.ctx.getState().itemsPerPage;
        const skip = limit * (this.ctx.getState().currentPage - 1);
        const search = this.ctx.getState().search;
        const mediaTypes = this.ctx.getState().mediaType.selectedMediaTypes;
        const order = this.ctx.getState().order;
        const sort = this.ctx.getState().sort;
        const currentDir = this.ctx.getState().currentDirectory?.id as number;
        const onlyMedias = false;
        const onlyFolders = this.ctx.getState().onlyFolders;

        this.ctx.patchState({loader: true});
        return this.mediaService.fetchFolderContent(currentDir, {
            limit, skip, sort, order
        }, mediaTypes, search, onlyMedias, onlyFolders)
            .pipe(
                finalize(() => this.ctx.patchState({loader: false})),
                tap((content) => {
                    const state = this.ctx.getState();
                    this.ctx.setState({
                        ...state, medias: content.content,
                        folders: content.folders, totalMedia: content.itemsCount
                    });
                })
            );
    }

    @DataActionWithoutSubscription()
    searchMedias(@Payload('[Modal] search medias') search: string): Observable<any> {
        this.ctx.patchState({search, currentPage: 1});
        return this.fetchMedia();
    }

    @DataActionWithoutSubscription()
    getMediasByPage(@Payload('[Modal] get medias by page') page: number): Observable<any> {
        this.ctx.patchState({currentPage: page});
        return this.fetchMedia();
    }

    @DataActionWithoutSubscription()
    getMediasByType(@Payload('[Modal] get medias by type') activeTab: MediaTypeEnum | '', selectedTypes: MediaTypeEnum[] | ''): void {
        const onlyMedias = !!activeTab;
        this.ctx.patchState({
            mediaType: {activeTabMediaType: activeTab, selectedMediaTypes: selectedTypes},
            onlyMedias,
            search: '',
            currentPage: 1
        });
    }

    @DataActionWithoutSubscription()
    toggleFoldersMode(@Payload('[Modal] set folders mode') onlyFolders: boolean): void {
        this.ctx.patchState({onlyFolders});
    }

    @DataActionWithoutSubscription()
    setMediasPerPageCount(@Payload('[Modal] set medias per page count') num: number): void {
        this.ctx.patchState({itemsPerPage: num});
    }

    @DataActionWithoutSubscription()
    setCurrentDirectory(@Payload('[Modal] set current directory') dir: IFolderPath): void {
        this.ctx.patchState({currentDirectory: dir, currentPage: 1, search: ''});
    }

    @DataActionWithoutSubscription()
    sortContent(@Payload('[Modal] sort content') sort: MediaSortType, order: OrderType): Observable<any> {
        this.ctx.patchState({sort, order});
        return this.fetchMedia();
    }
}
